// @file Composable to encapsulate post duplicate and transfer logic

import { trackEvent } from '@@/bits/analytics'
import { ALERT_SIGN_EMOJI } from '@@/bits/emoji'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { navigateTo } from '@@/bits/location'
import { hasCustomProperties } from '@@/bits/post_properties'
import { useGlobalConfirmationDialogStore } from '@@/pinia/global_confirmation_dialog'
import type { GlobalSnackbarNotification } from '@@/pinia/global_snackbar'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfacePostActionStore } from '@@/pinia/surface_post_action'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { HashId, Wall } from '@@/types'
import { createSharedComposable } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'

const usePostDuplicateAndTransfer = createSharedComposable(
  (): {
    duplicatePost: typeof duplicatePost
    transferPost: typeof transferPost
    canPostBeCopiedOrTransferred: typeof canPostBeCopiedOrTransferred
    checkIfPostCanBeCopiedOrTransferred: typeof checkIfPostCanBeCopiedOrTransferred
    isCopyingPost: typeof isCopyingPost
    isTransferringPost: typeof isTransferringPost
  } => {
    const surfacePostsStore = useSurfacePostsStore()
    const surfacePostActionStore = useSurfacePostActionStore()
    const globalConfirmationDialogStore = useGlobalConfirmationDialogStore()
    const { copyOrTransferPostResult, xCopyPostPanel, xTransferPostPanel } = storeToRefs(surfacePostActionStore)

    const canPostBeCopiedOrTransferred = ref(false)
    const isTransferringPost = computed(() => canPostBeCopiedOrTransferred.value && xTransferPostPanel.value)
    const isCopyingPost = computed(() => canPostBeCopiedOrTransferred.value && xCopyPostPanel.value)
    const postUnderIntent = computed(() =>
      xCopyPostPanel.value ? surfacePostActionStore.postBeingCopied : surfacePostActionStore.postBeingTransferred,
    )

    const checkIfPostCanBeCopiedOrTransferred = (): void => {
      canPostBeCopiedOrTransferred.value = false

      if (postUnderIntent.value == null || (!xCopyPostPanel.value && !xTransferPostPanel.value)) {
        return
      }

      const currentPostAction = xCopyPostPanel.value ? 'copy' : 'transfer'
      if (hasCustomProperties(postUnderIntent.value)) {
        const title = __('Only matching custom fields will transfer.')
        const body = __(
          'Custom fields that have the same name and data type as the receiving padlet will be transferred. All other custom fields will be deleted.',
        )

        globalConfirmationDialogStore.openConfirmationDialog({
          iconSrc: ALERT_SIGN_EMOJI.iconSrc,
          title,
          body,
          afterConfirmActions: [
            () => {
              canPostBeCopiedOrTransferred.value = true
            },
          ],
          afterCancelActions: [
            () => {
              canPostBeCopiedOrTransferred.value = false
              currentPostAction === 'copy'
                ? surfacePostActionStore.endPostCopy()
                : surfacePostActionStore.endPostTransfer()
            },
          ],
        })
      } else {
        canPostBeCopiedOrTransferred.value = true
      }
    }

    const duplicatePost = (wall: Wall, wallSectionHashid?: HashId): void => {
      if (surfacePostActionStore.postBeingCopiedCid == null) return
      const post = surfacePostsStore.postEntitiesByCid[surfacePostActionStore.postBeingCopiedCid]
      if (post == null || post.hashid == null || post.id == null) return

      const isDestinationThisWall = post.wall_hashid === wall.hashid
      if (isDestinationThisWall) {
        trackEvent('Posts', 'Copied post to the same or an existing padlet')
      } else {
        trackEvent('Posts', 'Copied post to a new padlet')
      }

      surfacePostActionStore.actuallyCopyPost({
        wallHashid: wall.hashid,
        wallSectionHashid,
        isDestinationThisWall,
        postId: post.id,
        postHashid: post.hashid,
        transferredPostCid: surfacePostActionStore.postBeingCopiedCid,
        transferCustomPropValuesAcrossWalls: !isDestinationThisWall,
      })

      surfacePostActionStore.endPostCopy()
    }

    const transferPost = (wall: Wall, wallSectionHashid?: HashId): void => {
      if (surfacePostActionStore.postBeingTransferredCid == null) return
      const post = surfacePostsStore.postEntitiesByCid[surfacePostActionStore.postBeingTransferredCid]
      if (post == null || post.hashid == null || post.id == null) return

      const isDestinationThisWall = post.wall_hashid === wall.hashid
      if (isDestinationThisWall) {
        // cannot transfer post to the same wall
        return
      } else {
        trackEvent('Posts', 'Transferred post to a new padlet')
      }
      surfacePostActionStore.actuallyTransferPost({
        wallHashid: wall.hashid,
        isDestinationThisWall,
        postId: post.id,
        postHashid: post.hashid,
        wallSectionHashid,
        transferredPostCid: surfacePostActionStore.postBeingTransferredCid,
        transferCustomPropValuesAcrossWalls: !isDestinationThisWall,
      })
      surfacePostActionStore.endPostTransfer()
    }

    if (isAppUsing('padletPickerV2')) {
      watch(copyOrTransferPostResult, (newResult: any) => {
        if (newResult == null) return
        let notification: GlobalSnackbarNotification = { message: newResult.text }

        if (newResult.status === 'error') {
          notification.notificationType = SnackbarNotificationType.error
        } else {
          if (newResult.isDestinationThisWall !== true) {
            notification = {
              ...notification,
              actionTextActions: [() => navigateTo(newResult.url)],
              actionText: __('Go to padlet'),
            }
          }
        }

        useGlobalSnackbarStore().setSnackbar(notification)
      })

      watch(
        [xCopyPostPanel, xTransferPostPanel, postUnderIntent],
        () => {
          checkIfPostCanBeCopiedOrTransferred()
        },
        { immediate: true },
      )
    }

    return {
      duplicatePost,
      transferPost,

      canPostBeCopiedOrTransferred,
      checkIfPostCanBeCopiedOrTransferred,

      isCopyingPost,
      isTransferringPost,
    }
  },
)

export default usePostDuplicateAndTransfer
